import type mongoose from "mongoose";
import { SensorPortCommunicationMode } from "../../Configurations/portConfigs.js";
import { sensorClusterConfigs } from "../../Configurations/SensorConfigurations/sensorClusters/sensorClusterConfigs.js";
import { ForeignLookup } from "../commonSubSchemas/foreignLookup.js";
import { SensorClusterAddress } from "../commonSubSchemas/sensorClusterAddress.js";
import {
    SensorCluster,
    type SensorClusterConfigKey,
    type SensorClusterOrigin,
} from "../sensorCluster/sensorCluster.js";
import { getDeviceTypeSpecificInformation } from "./deviceTypeSpecificInformation.js";
import { THIRD_PARTY_DEVICE_COLLECTION_NAME, type ThirdPartyDevice } from "./thirdPartyDevice.js";

export { getDisplayId } from "../commonGetters/getDisplayId.js";
export { getDisplayName } from "../commonGetters/getDisplayName.js";

/**
 * Builds the warning context object used when posting or clearing warnings for this device.
 *
 * `foreignLookup` identifies the device document in the warnings collection by `node_id`.
 * `targetInfo` carries human-readable metadata shown in warning notifications.
 * `owner` is used to scope warnings to the correct user account.
 */
export const getWarningContext = (self: ThirdPartyDevice) => ({
    foreignLookup: new ForeignLookup({
        targetId: self.node_id,
        targetForeignField: "node_id",
        targetCollection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
    }),
    targetInfo: {
        name: self.name ?? self.node_id,
        node_id: self.node_id,
        _id: (self as ThirdPartyDevice & { _id: mongoose.Types.ObjectId })._id,
    },
    owner: self.owner,
    aoi: self._aoi,
});

/**
 * Derives the SensorCluster documents that correspond to this ThirdPartyDevice.
 *
 * - For ePlant TreeTag devices, returns the main fixed cluster on port I1 (DIGITAL, channel 0)
 *   plus its battery cluster on the diagnostic port D1.
 * - For SDI-12 node devices, returns a cluster on port S1 derived from the device's SDI12NodeType.
 * - For Davis WeatherLink stations, returns one cluster per discovered sensor that resolved to a
 *   cluster config (see `deviceTypeSpecificInformation.sensors`), plus two fixed battery clusters — D1
 *   (console/gateway) and D2 (ISS transmitter) — mirroring ePlant's fixed main+battery shape, since
 *   neither battery is an independently discoverable WeatherLink "sensor".
 *
 * Returns an empty array if the device has no owner, or (for SDI-12 devices) if no matching
 * cluster config is found.
 */
export const getSensorClusters = (device: ThirdPartyDevice): SensorCluster[] => {
    if (!device.owner) return [];

    const hostReference = new ForeignLookup({
        targetId: device.node_id,
        targetForeignField: "node_id",
        targetCollection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
    });

    if (device.deviceType === "ePlantTreeTag") {
        const cluster = new SensorCluster();
        Object.assign(cluster, {
            address: new SensorClusterAddress({
                hostReference,
                portId: "I1",
                communicationMode: SensorPortCommunicationMode.DIGITAL,
                channel: 0,
            }),
            sensorClusterConfigKey: "ePlantTreeTag" as SensorClusterConfigKey,
            // I1 is not declared in this device type's sensorPortConfiguration; the origin is
            // asserted from device-type semantics — the TreeTag sensor is built into the device.
            origin: "fixedDevicePort" satisfies SensorClusterOrigin,
            parameters: {},
            owner: device.owner,
            connectedZones: device.connectedZones ?? [],
            position: device.position,
        });

        const batteryCluster = new SensorCluster();
        Object.assign(batteryCluster, {
            address: new SensorClusterAddress({
                hostReference,
                portId: "D1",
                communicationMode: SensorPortCommunicationMode.DIGITAL,
                channel: 0,
            }),
            sensorClusterConfigKey: "EPlantTreeTag_Battery" as SensorClusterConfigKey,
            origin: "fixedDevicePort" satisfies SensorClusterOrigin,
            parameters: {},
            owner: device.owner,
            connectedZones: device.connectedZones ?? [],
            position: device.position,
        });

        return [cluster, batteryCluster];
    }

    if (device.deviceType === "davisWeatherStation") {
        const buildCluster = (
            portId: string,
            channel: number,
            sensorClusterConfigKey: SensorClusterConfigKey,
            parameters: Record<string, number | string>,
        ): SensorCluster => {
            const cluster = new SensorCluster();
            Object.assign(cluster, {
                address: new SensorClusterAddress({
                    hostReference,
                    portId,
                    communicationMode: SensorPortCommunicationMode.DIGITAL,
                    channel,
                }),
                sensorClusterConfigKey,
                // These are the station's own onboard sensors, discovered via the WeatherLink API rather
                // than user-wired — not declared in sensorPortConfiguration, same as ePlant's I1 above.
                origin: "fixedDevicePort" satisfies SensorClusterOrigin,
                parameters,
                owner: device.owner,
                connectedZones: device.connectedZones ?? [],
                position: device.position,
            });
            return cluster;
        };

        const { sensors, recordingInterval } = getDeviceTypeSpecificInformation(device);

        // The station's archive rate, carried on every one of its clusters so that changing it on
        // the WeatherLink side rolls the whole station's fragments together rather than a subset.
        // It belongs on the readings, not just on the device: an archive record's fields are
        // aggregates over this span — `rainfall_mm` and `et` total across it, `temp_avg` and
        // `wind_speed_avg` are means over it, `wind_speed_hi` is the high of that many samples —
        // so the same stored number means a different thing at 5 minutes than at 15. The spot
        // readings (humidity, barometric pressure, the battery voltages) do not change meaning,
        // but they share the interval as their expected cadence, and aligning every fragment
        // boundary on the station is far easier to reason about than staggering them by field.
        // `null` is left out entirely rather than encoded, so a station that reports no interval
        // produces the same snapshot it did before this was recorded at all.
        const reportingInterval: Record<string, number> =
            typeof recordingInterval === "number" ? { reportingIntervalMinutes: recordingInterval } : {};

        const discoveredClusters = (sensors ?? []).flatMap((sensor) => {
            if (!sensor.sensorClusterConfigKey || !sensor.portId) return [];
            return [
                buildCluster(
                    sensor.portId,
                    sensor.channel ?? 0,
                    sensor.sensorClusterConfigKey as SensorClusterConfigKey,
                    {
                        lsid: sensor.lsid,
                        ...reportingInterval,
                    },
                ),
            ];
        });

        // Neither battery rides as its own discoverable WeatherLink sensor — each lives inside another
        // sensor's own /current record instead — so both clusters are fixed like ePlant's D1 rather than
        // derived from the sensors array. D1 is the station's own console/gateway battery (WLL health
        // telemetry, battery_voltage on sensor_type 504's lsid); D2 is the ISS transmitter's own battery
        // (trans_battery_flag/trans_battery_volt, on the ISS's own lsid) — a station reports each
        // independently of the other. Exactly which field feeds each cluster's readings is SWD-3309's.
        const consoleBatteryCluster = buildCluster("D1", 0, "Davis_WeatherLink_Battery" as SensorClusterConfigKey, {
            ...reportingInterval,
        });
        const transmitterBatteryCluster = buildCluster(
            "D2",
            0,
            "Davis_WeatherLink_TransmitterBattery" as SensorClusterConfigKey,
            { ...reportingInterval },
        );

        return [...discoveredClusters, consoleBatteryCluster, transmitterBatteryCluster];
    }

    // Reached only for device types whose deviceTypeSpecificInformation is still the untyped passthrough
    // (ePlantTreeTag and davisWeatherStation return above); the explicit cast keeps that read untyped
    // rather than resolving to a union with davisWeatherStation's now-typed shape.
    const SDI12NodeType = (getDeviceTypeSpecificInformation(device) as Record<string, any>).SDI12NodeType as
        | string
        | undefined;
    if (!SDI12NodeType) return [];

    const matchingEntry = Object.entries(sensorClusterConfigs).find(
        ([, config]) => config.sdi12NodeTypeKey === SDI12NodeType,
    );
    if (!matchingEntry) return [];

    const [sensorClusterConfigKey] = matchingEntry;

    const cluster = new SensorCluster();
    Object.assign(cluster, {
        address: new SensorClusterAddress({
            hostReference,
            // Always S1: the Dragino SDI-12 node device configuration defines a single sensor port named S1.
            portId: "S1",
            communicationMode: SensorPortCommunicationMode.SDI12,
            channel: 0,
        }),
        sensorClusterConfigKey: sensorClusterConfigKey as SensorClusterConfigKey,
        // The Dragino SDI-12 port is user-wired (its catalogue entry is `isFixed: false`).
        origin: "userConfigured" satisfies SensorClusterOrigin,
        parameters: {},
        owner: device.owner,
        connectedZones: device.connectedZones ?? [],
        position: device.position,
    });

    return [cluster];
};
